import { useCallback, useEffect, useState } from 'react';

const PRIVACY_MODE_STORAGE_KEY = 'hettwer:privacy-mode';
const PRIVACY_MODE_EVENT = 'hettwer:privacy-mode-changed';

type PrivacyStorageValue = 'visible' | 'hidden';

const resolvePrivacyMode = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.localStorage.getItem(PRIVACY_MODE_STORAGE_KEY) === 'hidden';
};

const emitPrivacyModeChange = (hidden: boolean): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(
    new CustomEvent<boolean>(PRIVACY_MODE_EVENT, {
      detail: hidden
    })
  );
};

export const readPrivacyMode = (): boolean => resolvePrivacyMode();

export const writePrivacyMode = (hidden: boolean): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const nextValue: PrivacyStorageValue = hidden ? 'hidden' : 'visible';
  window.localStorage.setItem(PRIVACY_MODE_STORAGE_KEY, nextValue);
  emitPrivacyModeChange(hidden);
};

export const usePrivacyMode = () => {
  const [isPrivacyMode, setIsPrivacyMode] = useState<boolean>(() => resolvePrivacyMode());

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key === PRIVACY_MODE_STORAGE_KEY) {
        setIsPrivacyMode(resolvePrivacyMode());
      }
    };

    const handlePrivacyModeChanged = (event: Event) => {
      const customEvent = event as CustomEvent<boolean>;
      setIsPrivacyMode(
        typeof customEvent.detail === 'boolean' ? customEvent.detail : resolvePrivacyMode()
      );
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener(PRIVACY_MODE_EVENT, handlePrivacyModeChanged as EventListener);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener(PRIVACY_MODE_EVENT, handlePrivacyModeChanged as EventListener);
    };
  }, []);

  const setPrivacyMode = useCallback((hidden: boolean) => {
    writePrivacyMode(hidden);
  }, []);

  const togglePrivacyMode = useCallback(() => {
    writePrivacyMode(!resolvePrivacyMode());
  }, []);

  return {
    isPrivacyMode,
    setPrivacyMode,
    togglePrivacyMode
  };
};

const SENSITIVE_BASE_CLASS = 'transition-all duration-200 ease-in-out';

export const getSensitiveFieldClassName = (hidden: boolean): string =>
  hidden ? `${SENSITIVE_BASE_CLASS} blur-sm select-none pointer-events-none` : SENSITIVE_BASE_CLASS;
